/**
 * PhotoboothTemplate.ts
 * Schéma TypeScript et Zod pour le format JSON runtime du photobooth
 */
import { z } from 'zod';

/**
 * Types d'éléments dans le template
 */
export type PhotoboothElementType = 'placeholder' | 'image' | 'text' | 'shape';

/**
 * Type de fond
 */
export type BackgroundType = 'color' | 'image' | 'transparent';

/**
 * Schéma Zod pour un élément de template
 */
export const PhotoboothElementSchema = z.object({
  type: z.enum(['placeholder', 'image', 'text', 'shape']),
  id: z.string().min(1, 'ID requis'),
  x: z.number(),
  y: z.number(),
  w: z.number().optional(),
  h: z.number().optional(),
  zIndex: z.number().min(0),
  // Propriétés spécifiques selon le type
  photoIndex: z.number().min(1).max(4).optional(), // Pour les placeholders
  src: z.string().optional(), // Pour les images
  content: z.string().optional(), // Pour les textes
  fill: z.string().optional(), // Pour les formes
  stroke: z.string().optional(),
  strokeWidth: z.number().optional(),
  fontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  rotation: z.number().optional(),
  opacity: z.number().min(0).max(1).optional(),
});

/**
 * Schéma Zod pour le fond
 */
export const BackgroundSchema = z.object({
  type: z.enum(['color', 'image', 'transparent']),
  value: z.string().optional(), // Couleur hex ou URL d'image
});

/**
 * Schéma Zod pour les métadonnées
 */
export const MetadataSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Version invalide (format: x.y.z)'),
  createdAt: z.string().datetime(),
  photoCount: z.enum(['1', '2', '3', '4'] as const),
  layout: z.enum(['1_portrait', '2_horizontal', '2_vertical', '3_strip', '4_grid']).optional(),
  author: z.string().optional(),
  description: z.string().optional(),
});

/**
 * Schéma Zod complet pour le template photobooth
 */
export const PhotoboothTemplateSchema = z.object({
  canvasSize: z.object({
    w: z.number().min(100).max(5000),
    h: z.number().min(100).max(5000),
  }),
  background: BackgroundSchema.optional(),
  elements: z.array(PhotoboothElementSchema).min(1, 'Au moins un élément requis'),
  metadata: MetadataSchema,
});

/**
 * Types TypeScript dérivés des schémas Zod
 */
export type PhotoboothElement = z.infer<typeof PhotoboothElementSchema>;
export type PhotoboothBackground = z.infer<typeof BackgroundSchema>;
export type PhotoboothMetadata = z.infer<typeof MetadataSchema>;
export type PhotoboothTemplate = z.infer<typeof PhotoboothTemplateSchema>;

/**
 * Interface pour faciliter la création de templates
 */
export interface PhotoboothTemplateBuilder {
  canvasSize: { w: number; h: number };
  background?: PhotoboothBackground;
  elements: PhotoboothElement[];
  metadata: PhotoboothMetadata;
}

/**
 * Valide un objet JSON comme template photobooth
 */
export function validatePhotoboothTemplate(
  data: unknown
): { success: true; data: PhotoboothTemplate } | { success: false; error: string } {
  try {
    const validated = PhotoboothTemplateSchema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessage = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', ');
      return { success: false, error: `Validation error: ${errorMessage}` };
    }
    return {
      success: false,
      error: `Unknown error: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
